import React, { FC, useState } from "react";
import { Controller } from "react-hook-form";
import { MessageDescriptor } from "react-intl";

import { Button } from "@UI/Components/Button/Button";
import { Heading } from "@UI/Components/Heading/Heading";
import { Icon } from "@UI/Components/Icon/Icon";
import { Envelope } from "@UI/Components/Icons/Envelope";
import { UserIcon } from "@UI/Components/Icons/UserIcon";
import {
  Input,
  InputGroup,
  InputLeftElement,
} from "@UI/Components/Input/Input";
import { Modal } from "@UI/Components/Modal/Modal";
import { PhoneInput } from "@UI/Components/PhoneInput/PhoneInput";
import { VStack } from "@UI/Components/Stack/Stack";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import useForm from "@Libs/Utils/hooks/useForm";
import { getCurrentCountry } from "@config";
import { TSsoSessionError } from "@modules/Authentication/services/ssoLogin";

import { AuthFormControl } from "../AuthFormControl/AuthFormControl";
import {
  authFieldStyles,
  authSubmitStyles,
} from "../AuthFormControl/authStyles";
import { EMAIL_PATTERN, NAME_PATTERN } from "../../config/constants";
import messages from "../../messages";

const FIELD_IDS = {
  email: "ssoMissingInfoEmail",
  firstName: "ssoMissingInfoFirstName",
  lastName: "ssoMissingInfoLastName",
  phone: "ssoMissingInfoPhone",
} as const;

export interface ISsoMissingInfoValues {
  firstName: string;
  lastName: string;
  /** E.164 from `PhoneInput`, or empty — the field is optional. */
  phoneNumber: string;
  /** Only collected when the provider withheld its own; empty otherwise. */
  email: string;
}

export interface ISsoMissingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Resolves to the refusal code on a retry still short of something. */
  onSubmit: (
    values: ISsoMissingInfoValues
  ) => Promise<TSsoSessionError | undefined>;
  /** What the provider did give, read back to the visitor and prefilled. */
  email?: string;
  /** True only for the identity itself, never for a stale prefill fetch. */
  emailMissing?: boolean;
  firstName?: string;
  lastName?: string;
}

/** The refusals this form can pin to the field that owns them. */
const FIELD_ERRORS: Partial<
  Record<
    TSsoSessionError,
    { field: keyof ISsoMissingInfoValues; message: MessageDescriptor }
  >
> = {
  firstname_missing: {
    field: "firstName",
    message: messages["taskCreation.auth.register.firstName.invalid"],
  },
  lastname_missing: {
    field: "lastName",
    message: messages["taskCreation.auth.register.lastName.invalid"],
  },
  no_email_received: {
    field: "email",
    message: messages["taskCreation.validation.required"],
  },
  phone_number_invalid: {
    field: "phoneNumber",
    message: messages["taskCreation.auth.register.phone.invalid"],
  },
};

/**
 * What the provider withheld, asked for once rather than sending the visitor
 * back to the registration form: Google sometimes answers with the email in
 * place of a name, Apple omits the name on every login after the first, and
 * Facebook lets the visitor decline to share an email at all.
 *
 * Both name fields are shown whenever either is missing — the endpoint names
 * one at a time, and a second round trip to collect the other would put this
 * same form back up. The email field only ever appears editable when the
 * provider gave none; otherwise it echoes back what it did give.
 */
export const SsoMissingInfoModal: FC<ISsoMissingInfoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  email,
  emailMissing,
  firstName,
  lastName,
}) => {
  const { formatMessage, locale } = useI18n();

  /**
   * `PhoneInput` validates the number itself and reports it here — the field is
   * optional, so only a number that was actually typed has to be valid.
   */
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ISsoMissingInfoValues>({
    // A name field's error should show on the blur that typed it, not wait
    // for a first submit attempt.
    mode: "onBlur",
    defaultValues: {
      firstName: firstName ?? "",
      lastName: lastName ?? "",
      phoneNumber: "",
      email: "",
    },
  });

  const handleValidSubmit = async (values: ISsoMissingInfoValues) => {
    if (values.phoneNumber && !isPhoneValid) {
      setError("phoneNumber", {
        message: formatMessage(
          messages["taskCreation.auth.register.phone.invalid"]
        ),
      });

      return;
    }

    const code = await onSubmit(values);
    const fieldError = code && FIELD_ERRORS[code];

    if (fieldError) {
      setError(fieldError.field, {
        message: formatMessage(fieldError.message),
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(handleValidSubmit)} noValidate>
        <VStack alignItems="stretch" spacing="16px">
          <Heading size="md" textAlign="center">
            <FormattedMessage
              {...messages["taskCreation.auth.sso.missingInfo.title"]}
            />
          </Heading>
          {emailMissing ? (
            <AuthFormControl
              htmlFor={FIELD_IDS.email}
              label={
                <FormattedMessage
                  {...messages["taskCreation.auth.email.label"]}
                />
              }
              errorMessage={errors.email?.message}
              isRequired
            >
              <InputGroup>
                <InputLeftElement
                  height={authFieldStyles.height}
                  pointerEvents="none"
                >
                  <Icon
                    as={Envelope}
                    width="16px"
                    height="16px"
                    color="gray.500"
                  />
                </InputLeftElement>
                <Input
                  id={FIELD_IDS.email}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  isDisabled={isSubmitting}
                  placeholder={formatMessage(
                    messages["taskCreation.auth.email.placeholder"]
                  )}
                  {...authFieldStyles}
                  {...register("email", {
                    required: formatMessage(
                      messages["taskCreation.validation.required"]
                    ),
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: formatMessage(
                        messages["taskCreation.auth.email.invalid"]
                      ),
                    },
                  })}
                />
              </InputGroup>
            </AuthFormControl>
          ) : (
            email && (
              <AuthFormControl
                htmlFor={FIELD_IDS.email}
                label={
                  <FormattedMessage
                    {...messages["taskCreation.auth.email.label"]}
                  />
                }
              >
                <InputGroup>
                  <InputLeftElement
                    height={authFieldStyles.height}
                    pointerEvents="none"
                  >
                    <Icon
                      as={Envelope}
                      width="16px"
                      height="16px"
                      color="gray.500"
                    />
                  </InputLeftElement>
                  {/* The address the provider reported. Read-only rather than
                      disabled, so it is still announced and copyable. */}
                  <Input
                    id={FIELD_IDS.email}
                    type="email"
                    value={email}
                    readOnly
                    {...authFieldStyles}
                    background="gray.100"
                    color="gray.600"
                  />
                </InputGroup>
              </AuthFormControl>
            )
          )}
          <AuthFormControl
            htmlFor={FIELD_IDS.firstName}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.register.firstName.label"]}
              />
            }
            errorMessage={errors.firstName?.message}
            isRequired
          >
            <InputGroup>
              <InputLeftElement
                height={authFieldStyles.height}
                pointerEvents="none"
              >
                <Icon
                  as={UserIcon}
                  width="16px"
                  height="16px"
                  color="gray.500"
                />
              </InputLeftElement>
              <Input
                id={FIELD_IDS.firstName}
                autoComplete="given-name"
                isDisabled={isSubmitting}
                placeholder={formatMessage(
                  messages["taskCreation.auth.register.firstName.placeholder"]
                )}
                {...authFieldStyles}
                {...register("firstName", {
                  required: formatMessage(
                    messages["taskCreation.auth.register.firstName.required"]
                  ),
                  pattern: {
                    value: NAME_PATTERN,
                    message: formatMessage(
                      messages["taskCreation.auth.register.firstName.invalid"]
                    ),
                  },
                })}
              />
            </InputGroup>
          </AuthFormControl>
          <AuthFormControl
            htmlFor={FIELD_IDS.lastName}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.register.lastName.label"]}
              />
            }
            errorMessage={errors.lastName?.message}
            isRequired
          >
            <InputGroup>
              <InputLeftElement
                height={authFieldStyles.height}
                pointerEvents="none"
              >
                <Icon
                  as={UserIcon}
                  width="16px"
                  height="16px"
                  color="gray.500"
                />
              </InputLeftElement>
              <Input
                id={FIELD_IDS.lastName}
                autoComplete="family-name"
                isDisabled={isSubmitting}
                placeholder={formatMessage(
                  messages["taskCreation.auth.register.lastName.placeholder"]
                )}
                {...authFieldStyles}
                {...register("lastName", {
                  required: formatMessage(
                    messages["taskCreation.auth.register.lastName.required"]
                  ),
                  pattern: {
                    value: NAME_PATTERN,
                    message: formatMessage(
                      messages["taskCreation.auth.register.lastName.invalid"]
                    ),
                  },
                })}
              />
            </InputGroup>
          </AuthFormControl>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <PhoneInput
                id={FIELD_IDS.phone}
                label={formatMessage(
                  messages["taskCreation.auth.register.phone.label"]
                )}
                initialCountry={getCurrentCountry()}
                height={authFieldStyles.height}
                onChange={field.onChange}
                onBlur={field.onBlur}
                onValidityChange={setIsPhoneValid}
                isError={Boolean(errors.phoneNumber)}
                errorMessage={errors.phoneNumber?.message}
                disabled={isSubmitting}
                locale={locale}
              />
            )}
          />
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            {...authSubmitStyles}
          >
            <FormattedMessage {...messages["taskCreation.cta.continue"]} />
          </Button>
        </VStack>
      </form>
    </Modal>
  );
};
